import {
	Box,
	Typography,
	useTheme,
	Stack,
	Button,
	TextField,
	CircularProgress,
	Grid,
} from '@mui/material';
import {
	useContext,
	useState,
	useEffect,
	useRef,
	forwardRef,
	useImperativeHandle,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { handleUpdateJournalChat } from '../../utils/api';
import { toast } from 'react-toastify';
import Positive from '../../svg/Positive';
import Negative from '../../svg/Negative';
import Neutral from '../../svg/Neutral';
import Happy from '../../svg/Happy';
import Sad from '../../svg/Sad';
import Anger from '../../svg/Anger';
import Fear from '../../svg/Fear';
import Surprise from '../../svg/Surprise';
import Disgust from '../../svg/Disgust';
import MicIcon from '@mui/icons-material/Mic';
import DeleteIcon from '../../svg/DeleteIcon';
import useSTT from './useSTT';
import AudioMeter from './AudioMeter';
import CONFIG from '../../config/config';

interface JournalEntryDetailsProps {
	entry: {
		docId?: string;
		title?: string;
		message?: string;
		character_count?: number;
		emotion?: string[];
		noted_at?: string;
		response?: string;
		room_id?: string;
		sentiment?: string;
		summary?: string;
		tags?: string[];
		updatedAt?: string;
		user_id?: string;
		word_count?: number;
	};
	screenMode: string;
	handleEditClick: () => void;
	authUserId: string;
	isSaveEnabled: boolean;
	setIsSaveEnabled: (enabled: boolean) => void;
	isSaving: boolean;
	setIsSaving: (saving: boolean) => void;
	isScreenMdOrLarger: boolean;
	isDeleting: boolean;
	handleDeleteClick: () => void;
}

export interface JournalEntryDetailsRef {
	saveDetails: () => Promise<void>;
}

const ScreenModes = {
	EMPTY_LIST: 'emptyList',
	DEFAULT_LIST: 'defaultList',
	VIEW_ENTRY: 'viewEntry',
	EDIT_ENTRY: 'editEntry',
};

const JournalDetailsEditForm = forwardRef<JournalEntryDetailsRef, JournalEntryDetailsProps>(
	(props, ref) => {
		const {
			entry,
			screenMode,
			handleEditClick,
			authUserId,
			isSaveEnabled,
			setIsSaveEnabled,
			isSaving,
			setIsSaving,
			isScreenMdOrLarger,
			isDeleting,
			handleDeleteClick,
		} = props;
		const [title, setTitle] = useState(entry.title || 'Title');
		const [content, setContent] = useState(entry.message);
		const navigate = useNavigate();
		const theme = useTheme();

		const {
			micAvailable,
			micPermissionAllowed,
			listening,
			transcribing,
			connecting,
			transcript,
			start,
			stop,
			clear,
			errorOccurred,
			errorMsg,
		} = useSTT();

		const lastLength = useRef(0);

		const saveDetails = async () => {
			setIsSaving(true);
			try {
				await updateJournalEntry(title, content);
			} catch (error) {
				console.log(error);
			}
		};

		useEffect(() => {
			setTitle(entry.title || 'Title');
			setContent(entry.message || '');
		}, [entry]);

		useEffect(() => {
			if (transcript.length > lastLength.current) {
				const newText = transcript.substring(lastLength.current);
				setContent((prev) => prev + newText);
				lastLength.current = transcript.length;

				const titleChanged = title !== entry.title;
				const contentChanged = (content + newText) !== entry.message;
				setIsSaveEnabled(titleChanged || contentChanged);
			}
		}, [transcript, title, content, entry.title, entry.message, setIsSaveEnabled]);

		const updateJournalEntry = async (title, content) => {
			try {
				let resp = await handleUpdateJournalChat(
					authUserId,
					title,
					content,
					entry.docId
				);
				if (resp) {
					toast.success('Updated Journal entry successfully');
					setIsSaving(false);
					navigate(0);
				} else {
					setIsSaving(true);
					toast.error('Unable to update journal entry');
				}
			} catch (err) {
				setIsSaving(false);
				console.log(err);
			}
		};

		useImperativeHandle(ref, () => ({
			saveDetails,
		}));

		const handleTitleChange = (e) => {
			const newTitle = e.target.value;
			setTitle(newTitle);
			const titleChanged = newTitle !== entry.title;
			const contentChanged = content !== entry.message;
			setIsSaveEnabled(titleChanged || contentChanged);
		};

		const handleContentChange = (e) => {
			const newContent = e.target.value;
			setContent(newContent);
			const titleChanged = title !== entry.title;
			const contentChanged = newContent !== entry.message;
			setIsSaveEnabled(titleChanged || contentChanged);
		};

		const handleRecord = () => {
			if (!listening) {
				lastLength.current = 0;
				clear();
				start();
			} else {
				stop();
			}
		};

		const recordDisabled = isSaving || connecting || !micAvailable || !micPermissionAllowed;

		if (!entry) {
			return (
				<Box
					display='flex'
					justifyContent='center'
					alignItems='center'
					height='100%'
				>
					<Typography variant='body1' color='text.secondary'>
						Select a journal entry to view details
					</Typography>
				</Box>
			);
		}

		const emotionsList = [
			{ name: 'happy', Icon: Happy },
			{ name: 'sad', Icon: Sad },
			{ name: 'surprise', Icon: Surprise },
			{ name: 'anger', Icon: Anger },
			{ name: 'fear', Icon: Fear },
			{ name: 'disgust', Icon: Disgust },
		];

		return (
			<Grid
				item
				xs={12}
				md={9}
				sx={{
					height: '100%',
					paddingLeft: { xs: '0px', md: '8px' },
				}}
			>
				<Stack
					direction='column'
					sx={{
						height: '100%',
						display: 'flex',
						flexDirection: 'column',
					}}
				>
					<Stack
						sx={{
							backgroundColor: theme.palette.background['paper-elevation-2'],
							border: `1px solid ${theme.palette.divider}`,
							borderRadius: '12px',
							padding: '24px',
							flex: 1,
							overflowY: 'auto',
							display: 'flex',
							flexDirection: 'column',
						}}
					>
						{screenMode === ScreenModes.VIEW_ENTRY ||
							screenMode === ScreenModes.DEFAULT_LIST ? (
							<Typography
								variant='h7'
								color='text.primary'
								sx={{
									maxLines: 1,
									textOverflow: 'ellipsis',
									fontSize: '1rem',
									fontStyle: 'normal',
									fontWeight: 600,
									lineHeight: '120%',
								}}
							>
								{entry.title || 'Untitled Entry'}
							</Typography>
						) : (
							<Box
								sx={{
									...(screenMode === ScreenModes.EDIT_ENTRY && {
										backgroundColor: theme.palette.primary._states.selected,
										border: `1px solid ${theme.palette.divider}`,
										paddingX: '4px',
										paddingY: '2px',
									}),
								}}
							>
								<TextField
									value={title}
									onChange={handleTitleChange}
									InputProps={{
										readOnly: listening || connecting,
									}}
									sx={{
										width: '100%',
										'& .MuiInputBase-input': {
											fontSize: '1rem',
											fontStyle: 'normal',
											fontWeight: 600,
											lineHeight: '120%',
											padding: '0px',
										},
										'& .MuiOutlinedInput-notchedOutline': {
											border: 'none',
										},
									}}
								/>
							</Box>
						)}

						<Typography
							variant='caption'
							color='text.secondary'
							sx={{
								maxLines: 1,
								textOverflow: 'ellipsis',
								pb: '24px',
							}}
						>
							{new Date(entry.noted_at)
								.toLocaleString('en-US', {
									month: 'short',
									day: 'numeric',
									year: 'numeric',
									hour: 'numeric',
									minute: '2-digit',
									hour12: true,
								})
								.replace(',', ' @')}
						</Typography>

						<Typography variant='h8' color='text.secondary'>
							Summary
						</Typography>

						<Typography
							variant='body1'
							color='text.primary'
							sx={{ pb: '24px' }}
						>
							{entry.summary || 'No summary available'}
						</Typography>

						{entry.emotion?.length > 0 && (
							<Stack
								direction='column'
								spacing={0.5}
								sx={{
									marginBottom: '24px',
								}}
							>
								<Typography variant='h8' color='text.secondary'>
									Topics
								</Typography>

								<Stack
									direction='row'
									spacing={1}
									sx={{
										display: 'flex',
										flexWrap: 'wrap',
										gap: 0.25,
									}}
								>
									{entry.tags
										.filter((tag, index, self) => self.indexOf(tag) === index)
										.map((tag, index) => (
											<Typography
												key={index}
												variant='body1'
												color='text.primary'
												sx={{
													border: 1,
													borderColor: 'divider',
													borderRadius: 2,
													py: 0.5,
													px: 1,
													margin: 0.5,
												}}
											>
												{tag}
											</Typography>
										))}
								</Stack>
							</Stack>
						)}

						{entry.sentiment && entry.sentiment !== '' && (
							<Stack
								direction={{ xs: 'column', sm: 'column', md: 'row' }}
								spacing={3}
								alignItems='left'
								marginBottom={3}
							>
								{entry.sentiment && (
									<Stack direction='column' spacing={1} alignItems='left'>
										<Typography variant='h8' color='text.secondary'>
											Sentiment
										</Typography>
										{entry.sentiment === 'positive' && (
											<Positive width='40px' height='40px' />
										)}
										{entry.sentiment === 'neutral' && (
											<Neutral width='40px' height='40px' />
										)}
										{entry.sentiment === 'negative' && (
											<Negative width='40px' height='40px' />
										)}
									</Stack>
								)}

								{entry.emotion?.length > 0 && (
									<Stack direction='column' spacing={1} alignItems='left'>
										<Typography variant='h8' color='text.secondary'>
											Emotions
										</Typography>
										<Stack direction='row' spacing={1}>
											{emotionsList
												.filter((emotion) =>
													entry.emotion.includes(emotion.name)
												)
												.map((emotion) => {
													const Icon = emotion.Icon;
													return (
														<Icon
															key={emotion.name}
															width='40px'
															height='40px'
															stroke={theme.palette.text.secondary}
														/>
													);
												})}
										</Stack>
									</Stack>
								)}
							</Stack>
						)}

						<Typography variant='h8' color='text.secondary'>
							Message
						</Typography>

						{screenMode === ScreenModes.VIEW_ENTRY ||
							screenMode === ScreenModes.DEFAULT_LIST ? (
							<Typography
								variant='body1'
								color='text.primary'
								sx={{ whiteSpace: 'pre-line', pb: '24px' }}
							>
								{entry.message || 'No content available'}
							</Typography>
						) : (
							<Box
								sx={{
									...(screenMode === ScreenModes.EDIT_ENTRY && {
										backgroundColor: theme.palette.primary._states.selected,
										border: `1px solid ${theme.palette.divider}`,
										paddingX: '4px',
										paddingY: '2px',
										marginTop: '4px',
									}),
								}}
							>
								<TextField
									multiline
									minRows={8}
									value={content}
									onChange={handleContentChange}
									InputProps={{
										readOnly: listening || connecting,
									}}
									sx={{
										width: '100%',
										'& .MuiInputBase-root': {
											padding: '0px !important',
										},
										'& .MuiInputBase-input': {
											fontSize: '1rem',
											fontWeight: 400,
											padding: '0px !important',
										},
										'& .MuiOutlinedInput-notchedOutline': {
											border: 'none',
										},
									}}
								/>
							</Box>
						)}
					</Stack>
					<Stack
						direction='row'
						justifyContent='space-between'
						mt='24px'
						mx='24px'
					>
						<Stack direction='row' spacing={2} alignItems='center'>

							<Button
								variant='text'
								size='large'
								startIcon={<DeleteIcon stroke={theme.palette.text.secondary} />}
								onClick={handleDeleteClick}
								style={{ color: theme.palette.text.secondary }}
								disabled={isSaving || listening || connecting}
							>
								Delete
							</Button>

							{screenMode === ScreenModes.EDIT_ENTRY && (
								<>
									<Button
										variant="contained"
										color={errorOccurred ? 'error' : 'secondary'}
										size="large"
										onClick={handleRecord}
										startIcon={
											listening && !connecting
												? (
													<Box
														sx={{
															width: 16,
															height: 16,
															border: `2px solid ${theme.palette.error.main}`,
															display: 'flex',
															alignItems: 'center',
															justifyContent: 'center',
															borderRadius: '2px',
														}}
													>
													</Box>
												)
												: <MicIcon />
										}
										disabled={recordDisabled || errorOccurred}
									>
										{errorOccurred
											? 'Error'
											: connecting
												? 'Connecting...'
												: listening
													? 'Stop Recording'
													: 'Record Voice'}
									</Button>

									<AudioMeter isListening={listening && !connecting} />
								</>
							)}

						</Stack>
						{screenMode === ScreenModes.VIEW_ENTRY ||
							screenMode === ScreenModes.DEFAULT_LIST ? (
							isScreenMdOrLarger ? (
								<Button
									variant='contained'
									color='secondary'
									size='large'
									onClick={handleEditClick}
								>
									Edit
								</Button>
							) : null
						) : (
							<></>
						)}
					</Stack>
				</Stack>
			</Grid>
		);
	}
);

export default JournalDetailsEditForm;
